import { useCallback, useRef } from 'react';

import { useDeepCompareEffect } from 'hooks/useDeepCompareEffect';
import {
  ShareMethodValueType,
  ticketDetailView,
  ticketInviteAccept,
  TicketInviteAcceptProperties,
  ticketInviteInvalid,
  TicketInviteInvalidProperties,
  ticketInviteView,
  TicketInviteViewProperties,
  ticketsOverviewView,
  ticketTransferConfirm,
  ticketTransferInitiate,
  ticketTransferRevoke,
  ticketTransferShare,
  ticketTransferTicketsSelectionView,
} from 'services/avo';
import { useTrackEvent, useTrackEventOnce } from 'services/datalayer/hooks';
import { StripAutomaticKeys } from 'services/datalayer/types';
import { mapPageEventToDatalayer } from 'services/datalayer/utils';
import { PageEventFragment } from 'types/api/dato-operations.types';
import { AccountTicketFragment } from 'types/api/tickets.types';

import { mapAccountTicketToDataLayer } from './utils';

export const useTicketDetailView = (
  event: PageEventFragment,
  tickets: Array<AccountTicketFragment>,
) => {
  const track = useTrackEvent({
    trackFn: ticketDetailView,
    variables: {
      ...mapPageEventToDatalayer(event),
      currency: 'SAR',
      value: 0,
    },
  });

  useDeepCompareEffect(() => {
    if (tickets.length) {
      track({
        items: tickets.map((ticket, idx) =>
          mapAccountTicketToDataLayer(ticket, event, idx),
        ),
      });
    }
  }, [tickets]);
};

export const useTicketsOverviewView = () => {
  const tracked = useRef<boolean>(false);
  const track = useTrackEvent({
    trackFn: ticketsOverviewView,
    variables: {},
  });

  return useCallback(
    (numActiveEvents: string) => {
      if (!tracked.current) {
        track({ numActiveEvents });
        tracked.current = true;
      }
    },
    [track],
  );
};

export const useTicketInviteView = (
  variables: StripAutomaticKeys<TicketInviteViewProperties>,
) =>
  useTrackEventOnce({
    trackFn: ticketInviteView,
    variables,
    withOrigin: false,
  });

export const useTicketInviteAccept = (
  variables: StripAutomaticKeys<TicketInviteAcceptProperties>,
) =>
  useTrackEventOnce({
    trackFn: ticketInviteAccept,
    variables,
    withOrigin: false,
  });

export const useTicketInviteInvalid = (
  variables: StripAutomaticKeys<TicketInviteInvalidProperties>,
) =>
  useTrackEventOnce({
    trackFn: ticketInviteInvalid,
    variables,
    withOrigin: false,
  });

export const useTrackTransferInitiate = () => {
  const tracked = useRef<boolean>(false);
  const track = useTrackEvent({
    trackFn: ticketTransferInitiate,
    variables: {},
  });

  return useCallback(() => {
    if (!tracked.current) {
      track({});
      tracked.current = true;
    }
  }, [track]);
};

export const useTrackTransferTicketsSelectionView = () => {
  const tracked = useRef<boolean>(false);
  const track = useTrackEvent({
    trackFn: ticketTransferTicketsSelectionView,
    variables: {},
  });

  return useCallback(() => {
    if (!tracked.current) {
      track({});
      tracked.current = true;
    }
  }, [track]);
};

export const useTrackTransferConfirm = () => {
  const tracked = useRef<boolean>(false);
  const track = useTrackEvent({
    trackFn: ticketTransferConfirm,
    variables: {},
  });

  return useCallback(() => {
    if (!tracked.current) {
      track({});
      tracked.current = true;
    }
  }, [track]);
};

export const useTrackTransferShare = () => {
  const track = useTrackEvent({
    trackFn: ticketTransferShare,
    variables: {},
  });

  return useCallback(
    (shareMethod: ShareMethodValueType) => {
      track({ shareMethod });
    },
    [track],
  );
};

export const useTrackTransferRevoke = () => {
  const tracked = useRef<boolean>(false);
  const track = useTrackEvent({
    trackFn: ticketTransferRevoke,
    variables: {},
  });

  return useCallback(() => {
    if (!tracked.current) {
      track({});
      tracked.current = true;
    }
  }, [track]);
};
